import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EmptyState } from './EmptyState';
import { SAMPLE_ARTIFACTS, Artifact, ArtifactStatus } from './artifacts/types';
import { ArtifactCard } from './artifacts/ArtifactCard';
import { ArtifactFullScreen } from './artifacts/ArtifactFullScreen';
type ActionStatus = 'running' | 'queued' | 'done';
interface Action {
  id: string;
  tool: string;
  description: string;
  status: ActionStatus;
  sourceTask?: string;
}
const INITIAL_ACTIONS: Action[] = [{
  id: 'a1',
  tool: 'email',
  description: 'Reply to Paul with privacy policy link',
  status: 'running',
  sourceTask: 'Paul \u2014 Privacy policy link'
}, {
  id: 'a2',
  tool: 'research',
  description: 'Compare token-bucket vs sliding window approaches',
  status: 'running',
  sourceTask: 'Tom \u2014 API rate limiting'
}, {
  id: 'a3',
  tool: 'draft',
  description: 'Write technical proposal for rate limiting',
  status: 'queued',
  sourceTask: 'Tom \u2014 API rate limiting'
}, {
  id: 'a4',
  tool: 'jira',
  description: 'Update ZEN-142 with approach and timeline',
  status: 'queued',
  sourceTask: 'Tom \u2014 API rate limiting'
}, {
  id: 'a5',
  tool: 'calendar',
  description: 'Schedule Acme partnership follow-up',
  status: 'queued',
  sourceTask: 'James \u2014 Acme contract'
}, {
  id: 'a6',
  tool: 'email',
  description: 'Reply to Sarah about Q2 budget',
  status: 'done',
  sourceTask: 'Sarah \u2014 Q2 budget approval'
}, {
  id: 'a7',
  tool: 'slack',
  description: "Approve Lisa's SSL cert PR",
  status: 'done',
  sourceTask: 'Lisa \u2014 SSL cert renewal'
}];
const STATUS_LABEL: Record<ActionStatus, string> = {
  running: 'running',
  queued: 'queued',
  done: 'done'
};
export function ActionsView() {
  const [actions] = useState(INITIAL_ACTIONS);
  const [artifacts, setArtifacts] = useState<Artifact[]>(SAMPLE_ARTIFACTS);
  const [expandedArtifact, setExpandedArtifact] = useState<Artifact | null>(null);
  const getActionArtifact = (actionId: string) => artifacts.find(a => a.actionId === actionId);
  const handleApprove = (id: string) => {
    setArtifacts(prev => prev.map(a => a.id === id ? {
      ...a,
      status: 'approved' as ArtifactStatus
    } : a));
  };
  const handleRefine = (id: string, _comment: string) => {
    setArtifacts(prev => prev.map(a => a.id === id ? {
      ...a,
      status: 'refining' as ArtifactStatus
    } : a));
    setTimeout(() => {
      setArtifacts(prev => prev.map(a => a.id === id ? {
        ...a,
        status: 'pending' as ArtifactStatus
      } : a));
    }, 2000);
  };
  const handleDiscard = (id: string) => {
    setArtifacts(prev => prev.map(a => a.id === id ? {
      ...a,
      status: 'discarded' as ArtifactStatus
    } : a));
  };
  if (actions.length === 0) {
    return <div className="min-h-[calc(100vh-56px)] w-full flex items-center justify-center px-6" data-id="element-10"><EmptyState message="Nothing to do." data-id="element-11" /></div>;
  }
  return <div className="min-h-[calc(100vh-56px)] pt-16 pb-32 px-6" data-id="element-12">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-13">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-10" data-id="element-14">Actions</h1>

        <div className="flex flex-col" data-id="element-15">
          {actions.map((action, i) => {
          const artifact = getActionArtifact(action.id);
          return <motion.div key={action.id} initial={{
            opacity: 0
          }} animate={{
            opacity: action.status === 'done' && !artifact ? 0.4 : 1
          }} transition={{
            duration: 0.4,
            delay: i * 0.03
          }} className="py-4" data-id="element-16">
                <div className="flex items-start gap-4" data-id="element-17">
                  <div className="flex-1 min-w-0" data-id="element-18">
                    <p className={`text-[15px] leading-[1.6] ${action.status === 'done' && !artifact ? 'text-[#8a8680]/50' : 'text-[#e8e4df]/90'}`} data-id="element-19">
                      {action.description}
                    </p>
                    {action.sourceTask && <button onClick={() => {
                  window.location.hash = '#/tasks';
                }} className="text-[12px] text-[#8a8680]/40 hover:text-[#8a8680] transition-colors mt-1" data-id="element-20">
                        {action.sourceTask}
                      </button>}
                  </div>
                  <span className={`text-[12px] shrink-0 mt-0.5 ${action.status === 'running' ? 'text-[#4a9a9a]/70' : 'text-[#8a8680]/40'}`} data-id="element-21">
                    {STATUS_LABEL[action.status]}
                  </span>
                </div>

                {artifact && <div className="mt-3 ml-0" data-id="element-22">
                    <ArtifactCard artifact={artifact} compact onExpand={() => setExpandedArtifact(artifact)} onApprove={() => handleApprove(artifact.id)} onRefine={comment => handleRefine(artifact.id, comment)} onDiscard={() => handleDiscard(artifact.id)} data-id="element-23" />
                  </div>}
              </motion.div>;
        })}
        </div>
      </div>

      <ArtifactFullScreen artifact={expandedArtifact} onClose={() => setExpandedArtifact(null)} onApprove={() => expandedArtifact && handleApprove(expandedArtifact.id)} onRefine={comment => expandedArtifact && handleRefine(expandedArtifact.id, comment)} onDiscard={() => expandedArtifact && handleDiscard(expandedArtifact.id)} data-id="element-24" />
    </div>;
}